import { blockAppearance, type BlockAppearance } from './blockAppearance';

export type Point = [number, number, number];
export type Direction = 'east' | 'west' | 'up' | 'down' | 'south' | 'north';
export const DIRECTIONS: Direction[] = ['east', 'west', 'up', 'down', 'south', 'north'];
export const NORMALS: Point[] = [[1,0,0],[-1,0,0],[0,1,0],[0,-1,0],[0,0,1],[0,0,-1]];
export interface ModelQuad {
  corners: number[];
  uv: number[];
  texture: string;
  cullface?: number;
  shade: number;
  tint?: number;
}
export interface ModelFace { texture: string; uv?: number[]; rotation?: number; cullface?: Direction; tintindex?: number }
export interface ModelElement {
  from: Point; to: Point;
  rotation?: { origin: Point; axis?: 'x' | 'y' | 'z'; angle?: number; x?: number; y?: number; z?: number; rescale?: boolean };
  shade?: boolean;
  faces: Partial<Record<Direction, ModelFace>>;
}
export interface JavaModel { parent?: string; textures?: Record<string, string | { sprite: string; force_translucent?: boolean }>; elements?: ModelElement[]; ambientocclusion?: boolean }
export interface ModelVariant { model: string; x?: number; y?: number; uvlock?: boolean; weight?: number }
export type ModelCondition = { OR?: ModelCondition[]; AND?: ModelCondition[]; [key: string]: string | ModelCondition[] | undefined };
export interface BlockState { variants?: Record<string, ModelVariant | ModelVariant[]>; multipart?: { when?: ModelCondition; apply: ModelVariant | ModelVariant[] }[] }
export interface ModelRegistry { models: Map<string, JavaModel>; states: Map<string, BlockState> }
export function resourceId(id: string): string { return id.includes(':') ? id : `minecraft:${id}`; }
export function appearanceKey(name: string, properties: Record<string,string> = {}): string {
  return resourceId(name) + JSON.stringify(Object.entries(properties).sort(([a], [b]) => a.localeCompare(b)));
}
export function facePoints(face: number, from: Point, to: Point): Point[] {
  const [x,y,z] = from, [X,Y,Z] = to;
  return [
    [[X,y,Z],[X,y,z],[X,Y,z],[X,Y,Z]], [[x,y,z],[x,y,Z],[x,Y,Z],[x,Y,z]],
    [[x,Y,Z],[X,Y,Z],[X,Y,z],[x,Y,z]], [[x,y,z],[X,y,z],[X,y,Z],[x,y,Z]],
    [[x,y,Z],[X,y,Z],[X,Y,Z],[x,Y,Z]], [[X,y,z],[x,y,z],[x,Y,z],[X,Y,z]],
  ][face] as Point[];
}
function defaultUv(face: number, from: Point, to: Point): number[] {
  const [x,y,z] = from, [X,Y,Z] = to;
  return [[16-Z,16-Y,16-z,16-y],[z,16-Y,Z,16-y],[x,z,X,Z],[x,16-Z,X,16-z],[x,16-Y,X,16-y],[16-X,16-Y,16-x,16-y]][face];
}
export function rotate(point: Point, axis: 'x'|'y'|'z', angle: number, origin: Point = [8,8,8]): Point {
  const p = point.map((v,i) => v-origin[i]);
  const a = axis === 'x' ? 1 : axis === 'y' ? 2 : 0, b = (a+1)%3;
  const c = Math.cos(angle*Math.PI/180), s = Math.sin(angle*Math.PI/180);
  const av = p[a], bv = p[b]; p[a] = av*c-bv*s; p[b] = av*s+bv*c;
  return p.map((v,i) => v+origin[i]) as Point;
}
function stateRotate(p: Point, v: ModelVariant, origin: Point = [8,8,8]): Point {
  return rotate(rotate(p,'x',-(v.x??0),origin),'y',-(v.y??0),origin);
}
function directionOf(p: Point): number {
  return NORMALS.findIndex((n) => n.every((v,i) => Math.abs(v-p[i]) < 0.0001));
}
function matches(when: ModelCondition, properties: Record<string,string>): boolean {
  return Object.entries(when).every(([key,value]) => {
    if (key === 'OR') return (value as ModelCondition[]).some((condition) => matches(condition, properties));
    if (key === 'AND') return (value as ModelCondition[]).every((condition) => matches(condition, properties));
    return typeof value === 'string' && value.split('|').includes(properties[key]);
  });
}
const defaults: Record<string,string> = {
  facing:'north', axis:'y', half:'bottom', type:'bottom', shape:'straight', rotation:'0',
  north:'false', south:'false', east:'false', west:'false', up:'true', down:'false',
  open:'false', powered:'false', lit:'false', waterlogged:'false', attached:'false', hanging:'false',
  hinge:'left', part:'foot', face:'floor', age:'0', stage:'0', level:'0', layers:'1', power:'0',
};
export function selectVariants(state: BlockState, given: Record<string,string>): ModelVariant[] {
  const properties = {...defaults,...given};
  const choose = (v: ModelVariant | ModelVariant[]) => Array.isArray(v) ? v[0] : v;
  if (state.variants) {
    const entries = Object.entries(state.variants);
    const compatible = entries.filter(([key]) => key.split(',').filter(Boolean).every((part) => {
      const [k,v] = part.split('='); return given[k] === undefined || v.split('|').includes(given[k]);
    }));
    // Missing states in legacy Bedrock exports use a consistent representative variant.
    compatible.sort(([a],[b]) => {
      const score = (key: string) => key.split(',').filter((p) => {const [k,v]=p.split('=');return properties[k]===v;}).length;
      return score(b)-score(a);
    });
    return compatible.length ? [choose(compatible[0][1])] : [];
  }
  if (state.multipart) {
    // Derive unspecified connection values from the values used by the definition.
    const infer = (condition: ModelCondition) => Object.entries(condition).forEach(([key,value]) => {
      if (Array.isArray(value)) value.forEach(infer);
      else if (given[key] === undefined && typeof value === 'string') {
        if (value.includes('none')) properties[key]='none';
        else if (properties[key] === undefined) properties[key]=value.split('|')[0];
      }
    });
    state.multipart.forEach(({when}) => when && infer(when));
    return state.multipart.filter(({when}) => !when || matches(when,properties)).map(({apply}) => choose(apply));
  }
  return [];
}
export function flattenModel(registry: ModelRegistry, id: string, chain: string[] = []): JavaModel {
  id = resourceId(id);
  if (chain.includes(id) || chain.length > 32) throw new Error(`Model parent cycle: ${id}`);
  const model = registry.models.get(id);
  if (!model) throw new Error(`Missing model: ${id}`);
  const parent = model.parent ? flattenModel(registry,model.parent,[...chain,id]) : {};
  return {...parent,...model,textures:{...parent.textures,...model.textures}};
}
function textureId(reference: string, textures: NonNullable<JavaModel['textures']>): string {
  if (!reference.startsWith('#') && textures[reference]) reference = '#'+reference;
  const visited = new Set<string>();
  while (reference.startsWith('#')) {
    if (visited.has(reference)) throw new Error(`Texture cycle: ${reference}`);
    visited.add(reference);
    const value = textures[reference.slice(1)];
    reference = typeof value === 'string' ? value : value?.sprite;
    if (!reference) throw new Error('Missing model texture');
  }
  return resourceId(reference);
}
function tintFor(name: string, properties: Record<string,string>): number {
  if (name.endsWith(':redstone_wire')) {
    const power = Math.max(0,Math.min(15,Number(properties.power??0)))/15;
    const r=power*0.6+(power>0?0.4:0.3),g=Math.max(0,power*power*0.7-0.5),b=Math.max(0,power*power*0.6-0.7);
    return (Math.round(r*255)<<16)|(Math.round(g*255)<<8)|Math.round(b*255);
  }
  if (name.includes('spruce_leaves')) return 0x619961;
  if (name.includes('birch_leaves')) return 0x80a755;
  if (name.includes('lily_pad')) return 0x208030;
  return /leaves|vine/.test(name) ? 0x77ab2f : 0x91bd59;
}
export function bakeModel(model: JavaModel, variant: ModelVariant, name: string, properties: Record<string,string>): ModelQuad[] {
  const quads: ModelQuad[]=[];
  for (const element of model.elements ?? []) {
    for (const [direction,face] of Object.entries(element.faces) as [Direction,ModelFace][]) {
      const faceIndex=DIRECTIONS.indexOf(direction);
      if (faceIndex<0) throw new Error(`Unknown model face: ${direction}`);
      let points=facePoints(faceIndex,element.from,element.to);
      const rotation=element.rotation;
      if(rotation) points=points.map((p)=> {
        let rotated=rotation.axis ? rotate(p,rotation.axis,rotation.angle??0,rotation.origin) : rotate(rotate(rotate(p,'x',rotation.x??0,rotation.origin),'y',rotation.y??0,rotation.origin),'z',rotation.z??0,rotation.origin);
        if(rotation.rescale && rotation.axis) rotated=rotated.map((v,i)=>i==='xyz'.indexOf(rotation.axis!)?v:rotation.origin[i]+(v-rotation.origin[i])/Math.cos((rotation.angle??0)*Math.PI/180)) as Point;
        return rotated;
      });
      points=points.map(p=>stateRotate(p,variant));
      const [u0,v0,u1,v1]=face.uv??defaultUv(faceIndex,element.from,element.to);
      let uv=[[u0,v1],[u1,v1],[u1,v0],[u0,v0]];
      const turns=(face.rotation??0)/90;
      uv=uv.map((_,i)=>uv[(i+turns)%4]);
      if(variant.uvlock) {
        const worldFace=directionOf(stateRotate(NORMALS[faceIndex],variant,[0,0,0]));
        if(worldFace>=0) {
          const canonical=facePoints(worldFace,[0,0,0],[16,16,16]);
          const original=facePoints(faceIndex,[0,0,0],[16,16,16]).map(p=>stateRotate(p,variant));
          const offset=canonical.findIndex(p=>p.every((v,i)=>Math.abs(v-original[0][i])<0.0001));
          if(offset>=0) uv=uv.map((_,i)=>uv[(i+offset)%4]);
        }
      }
      const a=points[0],b=points[1],c=points[2];
      const ab=b.map((v,i)=>v-a[i]),ac=c.map((v,i)=>v-a[i]);
      const n=[ab[1]*ac[2]-ab[2]*ac[1],ab[2]*ac[0]-ab[0]*ac[2],ab[0]*ac[1]-ab[1]*ac[0]];
      const length=Math.hypot(...n)||1;
      const shade=element.shade===false?1:Math.abs(n[0]/length)*0.6+Math.abs(n[2]/length)*0.8+Math.abs(n[1]/length)*(n[1]>=0?1:0.5);
      const culled=face.cullface?directionOf(stateRotate(NORMALS[DIRECTIONS.indexOf(face.cullface)],variant,[0,0,0])):-1;
      quads.push({corners:points.flat().map(v=>v/16),uv:uv.flat().map(v=>v/16),texture:textureId(face.texture,model.textures??{}),shade:Math.min(1,shade),...(culled>=0?{cullface:culled}:{}),...(face.tintindex!==undefined?{tint:tintFor(resourceId(name),properties)}:{})});
    }
  }
  return quads;
}
export function resolveJavaAppearance(registry: ModelRegistry,name: string,properties: Record<string,string> = {}): BlockAppearance | null {
  const state=registry.states.get(resourceId(name));
  if(!state) return null;
  const effective={...properties};
  if(name.endsWith('tripwire'))for(const d of ['north','south','east','west']) {if(effective[d]==='side')effective[d]='true';if(effective[d]==='none')effective[d]='false';}
  const variants=selectVariants(state,effective);
  if(!variants.length) return null;
  const quads: ModelQuad[]=[];
  for(const variant of variants) {
    const model=flattenModel(registry,variant.model);
    if(!model.elements && !/pitcher_crop_top_stage_[012]$/.test(variant.model)) return null; // Engine-rendered blocks are handled explicitly.
    quads.push(...bakeModel(model,variant,name,properties));
  }
  const appearance=blockAppearance(name,properties);
  return {...appearance,quads,cross:false,occludes:quads.length===6 && quads.every(q=>q.cullface!==undefined && q.corners.every(v=>v===0||v===1)) && appearance.layer==='solid' && !/leaves/.test(name),modelSource:'java'};
}
